// Native shell detection + external-link helper.
//
// A single source of truth for "am I running inside the iOS/Android
// Capacitor shell?" so every "Subscribe / Get Started" surface can hide
// itself and swap in the Reader-App-compliant external link instead.
// If we ever want to A/B test which platforms behave which way, this
// module is the one place to change.

import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";

/** True when the app is running inside iOS or Android native Capacitor shell. */
export function isNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// Tag the <body> as native so global CSS (`body[data-native="true"]`)
// can apply safe-area padding without every page opting in. Runs once
// at import time — safe on web (no-op when Capacitor isn't detected).
try {
  if (typeof document !== "undefined") {
    const native = Capacitor.isNativePlatform();
    document.body.setAttribute("data-native", native ? "true" : "false");
  }
} catch {}

export function nativePlatform(): "ios" | "android" | "web" {
  try {
    const p = Capacitor.getPlatform();
    return p === "ios" || p === "android" ? p : "web";
  } catch {
    return "web";
  }
}

/**
 * Open an external URL. On native we use `@capacitor/browser` which
 * launches SFSafariViewController on iOS and Custom Tabs on Android —
 * both are Apple/Google-approved for external links (as opposed to
 * shoving Stripe checkout into our WebView, which would violate rules).
 * On web we just navigate normally.
 */
export async function openExternal(url): Promise<void> {
  if (isNativeApp()) {
    try {
      await Browser.open({ url, presentationStyle: "popover" });
      return;
    } catch {
      /* fall through to window.open on any plugin failure */
    }
  }
  if (typeof window !== "undefined") {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/**
 * Convenience: opens the marketing / signup site externally so a user
 * without a paid account can subscribe on calofay.app (Stripe + coupons
 * + affiliate codes) then come back and log in.
 */
export function openSignupSite(reason = "signup") {
  const url = `https://calofay.app/?utm_source=app&utm_medium=${nativePlatform()}&utm_campaign=${reason}`;
  void openExternal(url);
}

// Auto-applied app promo: Android → CC10, iOS (GCC Stripe flow) → N10.
export function appPromoCode() {
  const p = nativePlatform();
  return p === "android" ? "CC10" : p === "web" ? "NEW10" : "N10";
}
